import { useEffect, useState } from 'react';
import { Control, Controller, useController } from 'react-hook-form';

import { Constant, ErrorType, Option, RegisterType } from 'types';

import { SelectMotives, pickMotive } from 'data/selectMotives.data';

import { Headline } from 'components/Headline';
import { Label } from 'components/Label';

import * as S from './SelectWithControl.styles';
import { StyledSelectProps } from './SelectWithControl.styles';

type SelectProps = {
  name: string;
  options: Option[];
  label?: string;
  defaultValue?: string | number | boolean;
  control: Control;
  selectMotive?: SelectMotives;
  disabled?: boolean;
  onInputChange?: (arg: string) => void;
  onSelect?: (arg: Option) => void;
  error?: ErrorType;
  required?: boolean;
  className?: string;
  ref?: RegisterType;
  isMulti?: boolean;
  asKeyValues?: boolean;
  helperText?: string;
} & StyledSelectProps;

const defaultProps: Partial<SelectProps> = {
  defaultValue: undefined,
  label: undefined,
  selectMotive: 'default',
  motive: 'default',
  disabled: false,
  isMulti: false,
  onSelect: undefined,
  error: undefined,
  required: false,
  className: undefined,
  asKeyValues: false,
  onInputChange: undefined,
  helperText: undefined,
};

export function SelectWithControl(props: SelectProps): React.ReactElement {
  const {
    options,
    label,
    control,
    selectMotive,
    motive,
    name,
    disabled,
    onSelect,
    onInputChange,
    error,
    scale,
    required,
    className,
    isMulti,
    asKeyValues,
    defaultValue,
    helperText,
  } = {
    ...defaultProps,
    ...props,
  };
  const [selectedValue, selectedValueSet] = useState<Option | Option[] | undefined>(undefined);

  const { field } = useController({ control, name });

  useEffect(() => {
    if (defaultValue !== undefined && !field.value) {
      const initialSelectedValue = options.find((option) => option.value === defaultValue);
      if (initialSelectedValue) {
        selectedValueSet(initialSelectedValue);
        field.onChange(initialSelectedValue.value);
      }
    } else if (field.value !== undefined) {
      const value = options.filter((item: Option) => {
        if (isMulti) {
          // field.value should be an array here (or a {key,value}[] when
          // asKeyValues), but some backend snapshots persist these fields
          // as a plain string/null instead (known data-shape mismatch —
          // see .claude/customer-mapper-context.md's Shop "analysis" fields
          // note) — .some()/.includes() would throw on that, crashing the
          // whole page (caught by ErrorBoundaryInner, which swallows the
          // error without logging it — hence no console/network trace).
          if (!Array.isArray(field.value)) return false;

          if (asKeyValues) {
            return field.value.some((fieldItem: Constant) => fieldItem.key === item.value);
          }
          return field.value.includes(item.value);
        }

        if (asKeyValues) {
          return typeof field.value === 'object' && field.value !== null && item.value === field.value.key;
        }
        return item.value === field.value;
      });

      if (isMulti && value.length === 0) {
        selectedValueSet([]);
      } else {
        selectedValueSet(value);
      }
    }
  }, [defaultValue, field.value, options, isMulti, asKeyValues]);

  const optionsWithDefault = [
    { value: '', label: 'Select...' },
    ...options.filter((option) => option.value !== ''),
  ];

  const optionsFiltered = optionsWithDefault.filter(
    (value, index, self) => index === self.findIndex((t) => t.label === value.label)
  );

  const motivePicked = disabled ? 'disabled' : selectMotive!;

  return (
    <S.Container motive={motive} scale={scale} className={className}>
      {label ? (
        <Label required={required} error={error}>
          {label}
        </Label>
      ) : null}

      <S.InnerContainer>
        <Controller
          control={control}
          name={name}
          rules={{
            required: required && `Please select ${label}`,
            validate: (val) => (val?.key !== '' ? undefined : `Please select ${label}`),
          }}
          defaultValue={defaultValue}
          render={({ field: { onChange, ref } }) => {
            const setOnChange = (val: Option | Option[]) => {
              if (Array.isArray(val)) {
                const selectValue = val.map((item: Option) =>
                  asKeyValues ? { key: item.value, value: item.label } : item.value
                );
                onChange(selectValue);
              } else {
                const keyValue = asKeyValues
                  ? { key: val.value, value: val.label }
                  : val.value;
                onChange(keyValue);
              }

              selectedValueSet(val);

              if (onSelect) {
                // @ts-ignore
                onSelect(val);
              }
            };

            return onInputChange ? (
              <S.Select
                styles={pickMotive(motivePicked)}
                ref={ref}
                value={selectedValue}
                isDisabled={disabled}
                options={optionsFiltered}
                onChange={(val: unknown) => setOnChange(val as Option | Option[])}
                onInputChange={(e) => onInputChange(e)}
                isMulti={isMulti}
              />
            ) : (
              <S.Select
                styles={pickMotive(motivePicked)}
                ref={ref}
                value={selectedValue}
                isDisabled={disabled}
                options={optionsFiltered}
                onChange={(val: unknown) => setOnChange(val as Option | Option[])}
                isMulti={isMulti}
              />
            );
          }}
        />
        {helperText && (
          <S.HelperText>
            <Headline scale="s" color="grayLabel">
              {helperText}
            </Headline>
          </S.HelperText>
        )}
      </S.InnerContainer>
    </S.Container>
  );
}
